import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, ChevronLeft, FileDown } from "lucide-react";
import { API } from "@/lib/api";

function DisplayValue({ value }) {
  if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) {
    return <span className="text-slate-300">—</span>;
  }
  if (Array.isArray(value)) return <span className="text-slate-800">{value.join(", ")}</span>;
  if (typeof value === "object" && value.type === "photo" && (value.url || value.path)) {
    return <img src={value.url ? `${API}${value.url}` : value.path} alt="upload" className="h-40 rounded-lg border border-slate-200 object-contain" />;
  }
  if (typeof value === "object" && value.mode) {
    if (value.mode === "draw") return <img src={value.data} alt="signature" className="h-16 rounded border border-slate-200 bg-white p-1" />;
    return <span className="text-2xl text-slate-900" style={{ fontFamily: "'Dancing Script', cursive" }}>{value.data}</span>;
  }
  return <span className="whitespace-pre-wrap text-slate-800">{String(value)}</span>;
}

export default function AdminFormView() {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const formId = searchParams.get("form") || undefined;
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [res, tpl] = await Promise.all([
          api.get(`/admin/forms/${userId}`, { params: formId ? { form_id: formId } : {} }),
          api.get("/admin/template"),
        ]);
        setForm(res.data);
        setSteps(tpl.data.steps || []);
      } catch (e) {
        toast.error("Could not load this submission.");
      } finally {
        setLoading(false);
      }
    })();
  }, [userId, formId]);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/admin/forms/${userId}/pdf`, { responseType: "blob", params: formId ? { form_id: formId } : {} });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `paperwork-${(form?.patient_name || "patient").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download PDF.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <SiteHeader />
        <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#0F52BA]" /></div>
      </div>
    );
  }

  const data = form?.data || {};

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteHeader />
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/admin")} data-testid="back-to-admin">
            <ChevronLeft className="mr-1 h-4 w-4" /> Back to submissions
          </Button>
          <Button onClick={downloadPdf} className="bg-[#0F52BA] hover:bg-[#0B3D8A]" data-testid="admin-detail-pdf">
            <FileDown className="mr-1.5 h-4 w-4" /> Download PDF
          </Button>
        </div>

        <div className="mb-6 rounded-xl border border-slate-200 bg-white p-6">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">{form?.patient_name || "Patient"}</h1>
          <p className="mt-1 text-sm text-slate-500">{form?.patient_email}</p>
          <span className={`mt-3 inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${form?.status === "submitted" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
            {form?.status === "submitted" ? "Submitted" : "Draft"}
          </span>
        </div>

        <div className="space-y-5">
          {steps.map((s) => {
            const section = data[s.key] || {};
            const hasAny = s.fields.some((f) => {
              const v = section[f.key];
              return v != null && v !== "" && !(Array.isArray(v) && v.length === 0);
            });
            return (
              <div key={s.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={`view-section-${s.id}`}>
                <div className="border-b border-slate-100 bg-slate-50/60 px-6 py-3">
                  <h2 className="font-semibold text-slate-900">{s.title}</h2>
                </div>
                {hasAny ? (
                  <dl className="divide-y divide-slate-100">
                    {s.fields.map((f) => (
                      <div key={f.key} className="grid grid-cols-1 gap-1 px-6 py-3 sm:grid-cols-3 sm:gap-4">
                        <dt className="text-sm font-medium text-slate-500">{f.label}</dt>
                        <dd className="text-sm sm:col-span-2"><DisplayValue value={section[f.key]} /></dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="px-6 py-4 text-sm text-slate-300">No information entered.</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
